// Copyright 2025 Sorama B.V.

import { CompositeUnit } from "units/CompositeUnit";
import { Energy, EnergyCost } from "units/Energy";
import { Mass, MassCost } from "units/Mass";
import { MassFlow } from "units/MassFlow";
import { Pressure } from "units/Pressure";
import { Unit } from "units/Unit";
import { UnitSystem } from "units/UnitSystem";
import { Volume, VolumeCost } from "units/Volume";
import { FlowEffort, VolumeFlow } from "units/VolumeFlow";

/**
 * Helper class for generating unit options for measurement properties
 */
export class UnitOptionsHelper {
    /**
     * Gets pressure unit options including the current unit and common alternatives
     * @param currentUnit - The current unit (will be added to the options such that the selected object reference is preserved)
     */
    public static getPressureUnitOptions(currentUnit: Unit): Unit[] {
        const units = [currentUnit];
        const options = [
            Pressure.pascal,
            Pressure.milliPascal,
            Pressure.kiloPascal,
            Pressure.bar,
            Pressure.psi,
            Pressure.inh2o,
        ];

        // Add units if they are not already included
        for (const option of options) {
            if (!units.some((u) => u.unit === option.unit)) {
                units.push(option);
            }
        }
        return units;
    }

    /**
     * Gets gas cost unit options based on unit system and currency
     * @param currentUnit - The current unit (will be changed to use the selected currency, and added to the options such that the selected object reference is preserved)
     * @param selectedCurrency - The selected currency unit
     * @param selectedUnitSystem - The selected unit system (Metric or Imperial)
     * @param leakRateUnit - The current leak rate unit; when mass-flow-based (g/h), returns mass-based gas cost options
     */
    public static getGasCostUnitOptions(
        currentUnit: CompositeUnit,
        selectedCurrency: Unit,
        selectedUnitSystem: UnitSystem,
        leakRateUnit?: Unit
    ): Unit[] {
        const isMassFlowLeakRate = leakRateUnit?.unitType === MassFlow;

        if (isMassFlowLeakRate) {
            const massUnits = [Mass.gram, Mass.kiloGram];
            const isCurrentMassBased = massUnits.some((u) => u.unit === currentUnit.denominator.unit);

            if (isCurrentMassBased) {
                const updatedCurrentUnit = new CompositeUnit(selectedCurrency, currentUnit.denominator, MassCost);
                const additionalUnits = massUnits
                    .filter((u) => u.unit !== currentUnit.denominator.unit)
                    .map((u) => new CompositeUnit(selectedCurrency, u, MassCost));
                return [updatedCurrentUnit, ...additionalUnits];
            } else {
                return massUnits.map((u) => new CompositeUnit(selectedCurrency, u, MassCost));
            }
        }

        // Volume-based options
        // Update current unit to use selected currency
        const updatedCurrentUnit = new CompositeUnit(selectedCurrency, currentUnit.denominator, currentUnit.unitType);

        let volumeUnits: Unit[] = [];
        if (selectedUnitSystem.name === "Metric") {
            volumeUnits = Volume.metricVolumeUnits;
        } else if (selectedUnitSystem.name === "Imperial") {
            volumeUnits = Volume.imperialVolumeUnits;
        }

        const additionalUnits = volumeUnits
            .filter((u) => u.unit !== currentUnit.denominator.unit)
            .map((u) => new CompositeUnit(selectedCurrency, u, VolumeCost));

        return [updatedCurrentUnit, ...additionalUnits];
    }

    /**
     * Gets electricity cost unit options based on currency
     * @param currentUnit - The current unit (will be changed to use the selected currency, and added to the options such that the selected object reference is preserved)
     * @param selectedCurrency - The selected currency unit
     */
    public static getElectricityCostUnitOptions(currentUnit: CompositeUnit, selectedCurrency: Unit): Unit[] {
        // Update current unit to use selected currency
        const updatedCurrentUnit = new CompositeUnit(selectedCurrency, currentUnit.denominator, currentUnit.unitType);

        // Generate the remaining energy units based on the selected currency
        const otherEnergyUnits = [Energy.kiloWattHours, Energy.megaWattHours]
            .filter((u) => u.unit !== currentUnit.denominator.unit)
            .map((u) => new CompositeUnit(selectedCurrency, u, EnergyCost));

        return [updatedCurrentUnit, ...otherEnergyUnits];
    }

    /**
     * Gets power ratio unit options based on unit system
     * @param currentUnit - The current unit (will be added to the options such that the selected object reference is preserved)
     * @param selectedUnitSystem - The selected unit system (Metric or Imperial)
     */
    public static getPowerRatioUnitOptions(currentUnit: Unit, selectedUnitSystem: UnitSystem): Unit[] {
        const units: Unit[] = [currentUnit];

        if (selectedUnitSystem.name === "Metric") {
            units.push(...FlowEffort.metricFlowEffortUnits.filter((u) => u.unit !== currentUnit.unit));
        } else if (selectedUnitSystem.name === "Imperial") {
            units.push(...FlowEffort.imperialFlowEffortUnits.filter((u) => u.unit !== currentUnit.unit));
        }

        return units;
    }

    /**
     * Gets leak rate unit options based on unit system
     * @param currentUnit - The current unit (will be added to the options such that the selected object reference is preserved)
     * @param selectedUnitSystem - The selected unit system (Metric or Imperial)
     */
    public static getLeakRateUnitOptions(currentUnit: Unit, selectedUnitSystem: UnitSystem): Unit[] {
        const units = [currentUnit];

        // Add MassFlow units (g/hour)
        if (MassFlow.gramsPerHour.unit !== currentUnit.unit) {
            units.push(MassFlow.gramsPerHour);
        }
        if (selectedUnitSystem.name === "Metric") {
            units.push(...VolumeFlow.metricFlowUnits.filter((u) => u.unit !== currentUnit.unit));
        } else if (selectedUnitSystem.name === "Imperial") {
            units.push(...VolumeFlow.imperialFlowUnits.filter((u) => u.unit !== currentUnit.unit));
        }

        return units;
    }
}
